import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Card from 'components/global/Card';
import ProgressBar from 'components/global/ProgressBar';
import Skeleton from 'components/global/Skeleton';
import Typography from 'components/global/Typography';

import useAxiosPrivate from 'hooks/useAxiosPrivate';

import AppLogger from 'utils/AppLogger';
import { colorCombinations } from 'utils/colors';

import * as S from './styled/ProgressCard.styled';

/**
 * @description A component that fetches data from a given URL and displays progress bars within cards.
 *              Each card represents a specific time and percentage value from the data.
 * @param {string} fetchURL - The URL to fetch data from.
 * @param {object} body - The filter object to send with the fetch request.
 * @param {number} loadingCardNumber - The number of loading cards to display while data is being fetched.
 * @param {Array} dataConfig - The configuration array specifying the mapping of time and percentage fields.
 *
 * Example of dataConfig:
 * [
 *   {
 * 	   timeLabel: 'averagePickupTime'
 *     time: 'avgPickup',
 * 	   percentageLabel: 'pickupRate'
 *     percentage: 'pickupPercentage',
 *     color: '#2196f3'
 *   }
 * ]
 *
 * Example usage of ProgressCard:
 * <ProgressCard
 *   fetchURL={ORDERS.GET_BULLET_PERFORMANCE}
 *   loadingCardNumber={3}
 *   dataConfig={[
 *     { timeLabel: 'averagePickupTime', time: 'avgPickup', percentageLabel: 'pickupRate',
 * 		 percentage: 'pickupPercentage', color: '#2196f3' }
 *   ]}
 * />
 */

const ProgressCard = ({ fetchURL, body, loadingCardNumber = 3, dataConfig }) => {
	const { t } = useTranslation();
	const axios = useAxiosPrivate();
	const [data, setData] = useState();
	const [components, setComponents] = useState();
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		axios
			.post(fetchURL, body)
			.then((response) => {
				setData(response.data);
				setComponents(generateComponents(response.data, dataConfig));
			})
			.catch((error) => AppLogger.error(error))
			.finally(() => setLoading(false));
	}, [body]);

	const LoadingCard = () => (
		<Card padding={2}>
			{loading && (
				<div>
					<Skeleton width="10rem" height="1.8rem" />
					<S.ProgressTitle>
						<Skeleton width="3rem" height="1.8rem" />
						<Skeleton width="3rem" height="1.8rem" />
					</S.ProgressTitle>
					<Skeleton height=".5rem" width="100%" style={{ margin: 'var(--margin-1) 0 0 0 ' }} />
				</div>
			)}
		</Card>
	);

	const SingleCard = ({ color, timeTitle, time, progressTitle, progress }) => (
		<div>
			<Card padding={2}>
				<Typography>
					{t(timeTitle)}: {time}
				</Typography>
				<S.ProgressTitle>
					<Typography fontWeight="500">{progressTitle}</Typography>
					<Typography fontWeight="700">{`${progress}%`}</Typography>
				</S.ProgressTitle>
				<ProgressBar
					style={{ margin: 'var(--margin-1) 0 0 0 ' }}
					thickness="medium"
					value={progress}
					color={color}
				/>
			</Card>
		</div>
	);

	if (loading)
		return (
			<div className="row row-cols-1 row-cols-lg-3 row-cols-md-2 row-cols-sm-1">
				{Array(loadingCardNumber)
					.fill(0)
					.map((_, index) => (
						<LoadingCard key={index} />
					))}
			</div>
		);

	return (
		<div className="row row-cols-1 row-cols-lg-3 row-cols-md-2 row-cols-sm-1">
			{components.map((component, index) => (
				<SingleCard
					key={index}
					color={component.color ? component.color : colorCombinations[index].color}
					timeTitle={component.timeLabel}
					progressTitle={component.percentageLabel}
					progress={data[component.percentage]}
					time={data[component.time]}
				/>
			))}
		</div>
	);
};

export default ProgressCard;

/**
 * generateComponents
 * @description Filters the dataConfig array to include only the configuration objects
 *              that have corresponding keys present in the provided data object.
 * @param {object} data - The data object containing time and percentage values.
 * @param {Array} dataConfig - The configuration array specifying the mapping of time and percentage fields.
 * @returns {Array} A filtered array of configuration objects where both time and percentage keys are present in the data object.
 */
const generateComponents = (data, dataConfig) =>
	dataConfig.filter((config) => config.time in data && config.percentage in data);
